import {
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useId } from "react";
import { useTranslations } from "use-intl";

import type { GetFlightAvailabilityTokenAdditionalService } from "@/types/codegen";
import { formatCurrency } from "@/utils/currency";

type Props = {
  service: GetFlightAvailabilityTokenAdditionalService;
  mandatory: boolean;
  value: string;
  onChange: (optionId: string) => void;
};

export function ServiceSelect({ service, mandatory, value, onChange }: Props) {
  const labelId = useId();
  const t = useTranslations();

  const serviceNameKey = `flights.additionalServiceName.${service.service.categoryCode}`;
  const serviceDescriptionKey = `flights.additionalServiceDescription.${service.service.categoryCode}`;

  return (
    <FormControl className="w-full">
      <FormLabel
        id={labelId}
        sx={{
          fontWeight: "var(--font-weight-medium)",
          fontSize: "var(--text-sm)",
          lineHeight: "var(--line-height-sm)",
          color: "var(--color-text-title)",
        }}
      >
        {t.has(serviceNameKey)
          ? t(serviceNameKey)
          : service.service.categoryName}
      </FormLabel>
      <FormHelperText
        sx={{
          fontSize: "var(--text-xs)",
          lineHeight: "var(--line-height-xs)",
          color: "var(--color-text-tertiary)",
          margin: 0,
        }}
      >
        {t.has(serviceDescriptionKey)
          ? t(serviceDescriptionKey)
          : service.service.name}
      </FormHelperText>
      {mandatory ? (
        <FormHelperText
          sx={{
            fontSize: "var(--text-xs)",
            lineHeight: "var(--line-height-xs)",
            color: "var(--color-semantic-error-strong)",
            margin: 0,
            marginTop: "calc(var(--spacing)*2)",
          }}
        >
          {t("travellers.mandatoryService")}
        </FormHelperText>
      ) : null}
      <Select
        fullWidth
        value={value}
        labelId={labelId}
        onChange={(event) => onChange(event.target.value)}
        sx={{
          backgroundColor: "var(--color-white)",
          marginTop: "calc(var(--spacing)*2)",
        }}
        displayEmpty
      >
        <MenuItem value="">-</MenuItem>
        {service.options.map((option) => (
          <MenuItem
            key={option.code}
            value={option.code}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <span className="whitespace-normal">
              {/* remove currency info from the description */}
              {/* example: 1 bags - 23Kg total(76.98 USD) => 1 bags - 23Kg total */}
              {option.description.replace(/\([^)]*[A-Z]{3}\)$/, "").trim()}
            </span>{" "}
            {option.price.amount != null && option.price.currency ? (
              <span>
                +
                {formatCurrency(
                  option.price.amount,
                  option.price.currency,
                  option.price.currency !== "IQD"
                    ? { maximumFractionDigits: 2 }
                    : undefined,
                )}
              </span>
            ) : null}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
